import axios, {AxiosRequestConfig} from 'axios';
import {Readable} from 'stream';
import {checkConfig} from '@epic-node-services/common';

import {
    Timescales,
    PerfSource,
    DefaultParams,
    PeakDataItem,
    AvgDataItem,
    DataItemType,
    ComparisonDataItemType,
    TopIslandsDataItem,
    PlaytimeParams,
    PlaytimeDataItemType,
    PlaytimeTimescales,
    RetentionDataItem,
    ExternalTotalsDataItem,
    ExternalTotalsResponseDataItem,
    ExternalTotalsParams,
    PayoutResponseDataItem,
    EngagementPayoutDataItem,
    SacPayoutDataItem,
    AccoladeDataItem,
    AccoladeResponseDataItem,
    AccoladeMetadata,
    AccoladeMetadataResponse,
    PerformanceMetricDataItem,
    PerformanceMetricResponseDataItem,
    PlaytimeResponseDataItem,
    PlaytimeDataItem
} from './types';

const DATA_ROW_DELIMITER = '\n';
const FAILED_CALIBRATION_STATUS = 'Failed';
const timeout = 30000; // Todo: remove this after improving valkyrie performance
const perfSourceMap = {
    [Timescales.hourly]: PerfSource.hourly,
    [Timescales.daily]: PerfSource.daily
};

export class ValkyrieMetricsService {
    private adminUrl;
    private publicUrl;
    private username;
    private secret;
    private accoladesSource;

    public configure(envConfig) {
        checkConfig(
            'ValkyrieService',
            envConfig,
            'EPIC_SERVICE_LINKS_ADMIN_URL',
            'EPIC_SERVICE_VALKYRIE_METRICS_PUBLIC_URL',
            'EPIC_SERVICE_VALKYRIE_METRICS_CLIENT_ID',
            'EPIC_SERVICE_VALKYRIE_METRICS_CLIENT_SECRET',
            'EPIC_ACCOLADES_SOURCE'
        );

        this.adminUrl = envConfig.EPIC_SERVICE_LINKS_ADMIN_URL;
        this.publicUrl = envConfig.EPIC_SERVICE_VALKYRIE_METRICS_PUBLIC_URL;
        this.username = envConfig.EPIC_SERVICE_VALKYRIE_METRICS_CLIENT_ID;
        this.secret = envConfig.EPIC_SERVICE_VALKYRIE_METRICS_CLIENT_SECRET;
        this.accoladesSource = envConfig.EPIC_ACCOLADES_SOURCE;
    }

    private collectDataFromStream<T = any>(stream: Readable): Promise<T[]> {
        return new Promise((resolve, reject) => {
            let buffer = '';

            stream.on('data', data => {
                buffer += data.toString();
            });

            stream.on('error', e => {
                reject(e);
            });

            stream.on('end', () => {
                if (!buffer) {
                    resolve([]);
                    return;
                }

                const result = buffer.split(DATA_ROW_DELIMITER).reduce((acc, chunk) => {
                    if (chunk) {
                        try {
                            acc.push(JSON.parse(chunk));
                        } catch (e) {
                            console.error(
                                'ValkyrieMetricsService.collectDataFromStream() =>  JSON parsing error: ',
                                chunk
                            );
                            reject(e);
                        }
                    }

                    return acc;
                }, []);

                resolve(result);
            });
        });
    }

    private getRequestConfig<T = DefaultParams>(params?: T, ...restParams): AxiosRequestConfig {
        return {
            responseType: 'stream',
            auth: {
                username: this.username,
                password: this.secret
            },
            timeout,
            params,
            ...restParams
        };
    }

    public async getTotalActiveUsers<T extends Timescales>(
        accountId: string,
        timescale: T,
        params: DefaultParams
    ): Promise<{
        status: number;
        data: DataItemType<T>[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/ccu/${timescale}/${accountId}/`,
                this.getRequestConfig(params)
            );

            const data = await this.collectDataFromStream<DataItemType<T>>(stream);

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error(
                    'ValkyrieMetricsService.getTotalActiveUsers() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getPeak(
        accountId: string,
        params?: DefaultParams
    ): Promise<{
        status: number;
        data: PeakDataItem[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/peak/live/${accountId}/`,
                this.getRequestConfig(params)
            );

            const data = await this.collectDataFromStream<PeakDataItem>(stream);

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error('ValkyrieMetricsService.getPeak() => error response: ', status, data);
            }
            throw new Error(ex.message);
        }
    }

    public async getAvg(
        accountId: string,
        params?: DefaultParams
    ): Promise<{
        status: number;
        data: AvgDataItem[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/avg/live/${accountId}/`,
                this.getRequestConfig(params)
            );

            const data = await this.collectDataFromStream<AvgDataItem>(stream);

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error('ValkyrieMetricsService.getAvg() => error response: ', status, data);
            }
            throw new Error(ex.message);
        }
    }

    public async getComparison<T extends Timescales>(
        accountId: string,
        timescale: T,
        params: DefaultParams
    ): Promise<{
        status: number;
        data: ComparisonDataItemType<T>[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/compare/${timescale}/${accountId}/`,
                this.getRequestConfig(params)
            );

            const data = await this.collectDataFromStream<ComparisonDataItemType<T>>(stream);

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error(
                    'ValkyrieMetricsService.getComparison() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getTopIslands<T extends Timescales>(
        accountId: string,
        timescale: T,
        params: DefaultParams
    ): Promise<{
        status: number;
        data: TopIslandsDataItem[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/top/${timescale}/${accountId}`,
                this.getRequestConfig(params)
            );

            const data = await this.collectDataFromStream<TopIslandsDataItem>(stream);

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error(
                    'ValkyrieMetricsService.getTopIslands() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getPlayTime<T extends PlaytimeTimescales>(
        accountId: string,
        timescale: T,
        params: PlaytimeParams
    ): Promise<{
        status: number;
        data: PlaytimeDataItemType<T>[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/playtime/${timescale}/${accountId}/`,
                this.getRequestConfig(params)
            );

            const data = await this.collectDataFromStream<PlaytimeDataItemType<T>>(stream);

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error(
                    'ValkyrieMetricsService.getPlayTime() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getRetention(
        accountId: string,
        params?: DefaultParams
    ): Promise<{
        status: number;
        data: RetentionDataItem[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/retention/dau/${accountId}`,
                this.getRequestConfig(params)
            );

            const data = await this.collectDataFromStream<RetentionDataItem>(stream);

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;

                console.error(
                    'ValkyrieMetricsService.getRetention() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getExternalTotals(
        accountId: string,
        params?: ExternalTotalsParams
    ): Promise<{
        status: number;
        data: ExternalTotalsDataItem[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/external/totals_v1/${accountId}`,
                this.getRequestConfig(params)
            );

            const rawData = await this.collectDataFromStream<ExternalTotalsResponseDataItem>(
                stream
            );
            const data = rawData.map(item => {
                try {
                    return {
                        ...item,
                        payload: JSON.parse(item.payload) as ExternalTotalsDataItem['payload']
                    };
                } catch (error) {
                    return {
                        ...item,
                        payload: {total_plays: 0, unique_users: 0}
                    };
                }
            });

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;

                console.error(
                    'ValkyrieMetricsService.getExternalTotals() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getEngagementPayouts(
        accountId: string,
        params: DefaultParams
    ): Promise<{
        status: number;
        data: EngagementPayoutDataItem[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/external/earnings_v1/${accountId}/`,
                this.getRequestConfig(params)
            );

            const rawData = await this.collectDataFromStream<PayoutResponseDataItem>(stream);

            const data = rawData.map(item => {
                try {
                    return {
                        ...item,
                        payload: JSON.parse(item.payload) as EngagementPayoutDataItem['payload']
                    };
                } catch (error) {
                    return {
                        ...item,
                        payload: {earnings: 0}
                    };
                }
            });

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error(
                    'ValkyrieMetricsService.getEngagementPayouts() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getSacPayouts(
        accountId: string,
        params: DefaultParams
    ): Promise<{
        status: number;
        data: SacPayoutDataItem[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/external/sac_earnings_v1/${accountId}/`,
                this.getRequestConfig(params)
            );

            const rawData = await this.collectDataFromStream<PayoutResponseDataItem>(stream);

            const data = rawData.map(item => {
                try {
                    return {
                        ...item,
                        payload: JSON.parse(item.payload) as SacPayoutDataItem['payload']
                    };
                } catch (error) {
                    return {
                        ...item,
                        payload: {sac_earnings: 0}
                    };
                }
            });

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error(
                    'ValkyrieMetricsService.getSacPayouts() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getAccolades(
        accountId: string,
        params: DefaultParams
    ): Promise<{
        status: number;
        data: AccoladeDataItem[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/external/${this.accoladesSource}/${accountId}/`,
                this.getRequestConfig(params)
            );

            const rawData = await this.collectDataFromStream<AccoladeResponseDataItem>(stream);

            const data = rawData.map(({timestamp, payload}) => {
                try {
                    return {
                        timestamp,
                        payload: JSON.parse(payload) as AccoladeDataItem['payload']
                    };
                } catch (error) {
                    return {
                        timestamp,
                        payload: {accolades: {}, percentile: 0, versions: []}
                    };
                }
            });

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error(
                    'ValkyrieMetricsService.getAccolades() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getAccoladesMetadata(
        islandCode: string
    ): Promise<{
        status: number;
        data: AccoladeMetadata;
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.adminUrl}/admin/api/mnemonic/${islandCode}`,
                this.getRequestConfig({filter: false})
            );

            const rawData = await this.collectDataFromStream<AccoladeMetadataResponse>(stream);

            const {metadata: {_accolades, dynamicXp: {calibrationPhase} = {}} = {}} = rawData[0];
            const accolades = (_accolades || []).reduce((acc, {name, guid, description}) => {
                const id = guid.replace(/-/g, '');

                return {
                    ...acc,
                    [id]: {name, description}
                };
            }, {});

            const data = {
                accolades,
                calibration: calibrationPhase || FAILED_CALIBRATION_STATUS
            };

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error(
                    'ValkyrieMetricsService.getAccoladesMetadata() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getPerformanceMetrics(
        accountId: string,
        timescale: Timescales,
        params: DefaultParams
    ): Promise<{
        status: number;
        data: PerformanceMetricDataItem[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/external/${perfSourceMap[timescale]}/${accountId}/`,
                this.getRequestConfig(params)
            );

            const rawData = await this.collectDataFromStream<PerformanceMetricResponseDataItem>(
                stream
            );

            const data = rawData.map(({timestamp, payload}) => {
                try {
                    return {
                        timestamp,
                        payload: JSON.parse(payload) as PerformanceMetricDataItem['payload']
                    };
                } catch (error) {
                    return {
                        timestamp,
                        payload: {}
                    };
                }
            });

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;
                console.error(
                    'ValkyrieMetricsService.getPerformanceMetrics() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }

    public async getPlaytime(
        accountId: string,
        params?: DefaultParams
    ): Promise<{
        status: number;
        data: PlaytimeDataItem[];
    }> {
        try {
            const {status, data: stream} = await axios.get(
                `${this.publicUrl}/v1/metrics/external/playtime_v1/${accountId}`,
                this.getRequestConfig(params)
            );

            const rawData = await this.collectDataFromStream<PlaytimeResponseDataItem>(stream);

            const data = rawData.map(({creative_island_code, payload}) => {
                try {
                    return {
                        creative_island_code,
                        payload: JSON.parse(payload) as PlaytimeDataItem['payload']
                    };
                } catch (error) {
                    return {
                        creative_island_code,
                        payload: {
                            as_of: '',
                            per_version: {}
                        }
                    };
                }
            });

            return {status, data};
        } catch (ex) {
            if (ex.response) {
                const {
                    response: {status, data}
                } = ex;

                console.error(
                    'ValkyrieMetricsService.getPlaytime() => error response: ',
                    status,
                    data
                );
            }
            throw new Error(ex.message);
        }
    }
}

export default new ValkyrieMetricsService();
